import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  subscribeToUserNotifications,
  updateNotificationAsRead,
} from "config/firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Pagination from "components/Pagination";
import { useUserStore } from "store/userStore";
import { isAdminRole } from "utils/roles";
import { resolveNotificationTarget } from "utils/notificationRouting";
import { useEnrichRequestAnsweredNotifications } from "hooks/useEnrichRequestAnsweredNotifications";
import { buildNotificationMetaLine, stripFlightMention } from "utils/notificationText";

const DEFAULT_PAGE_SIZE = 10;

interface NotificationItem {
  avatar: string;
  username: string;
  responderName: string;
  id: string;
  title: string;
  message: string;
  time: string;
  isNew: boolean;
  type?: string;
  requestDetailId?: number | null;
  // Not written by the backend yet — see notif.flightNumber mapping below.
  flightNumber?: string;
}

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);

  if (minutes < 1) return "0 min";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""}`;

  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? "s" : ""}`;
};

const mapNotifications = (raw: any[]): NotificationItem[] =>
  raw
    .map((notif: any) => ({
      avatar: "",
      id: notif.id,
      username: notif.responderName || "Anonymous",
      responderName: notif.responderName || "Anonymous",
      title: notif.title || "",
      message: notif.message || "",
      time: notif.createdAt?.toDate?.().toISOString() || "",
      isNew: !notif.IsRead,
      type: notif.type,
      requestDetailId: notif.requestDetailId ?? null,
      flightNumber: notif.flightNumber || "",
    }))
    .sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );

const getInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) {
    return `${parts[0][0]}${parts[0].slice(-1)}`.toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const userRole = useUserStore((state) => state.role);
  const collectionName = isAdminRole(userRole) ? "admin_notifications" : "load_notifications";

  const auth = getAuth();

  const totalPages = Math.max(1, Math.ceil(notifications.length / pageSize));

  // Notifications arrive via a realtime subscription, so the list can shrink
  // (e.g. on another device). Keep the current page within range.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedNotifications = notifications.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  useEnrichRequestAnsweredNotifications(notifications, setNotifications);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        const unsubscribeNotifications = subscribeToUserNotifications(
          user.uid,
          (fetchedNotifications) => {
            setNotifications(mapNotifications(fetchedNotifications));
          },
          collectionName
        );
        return () => unsubscribeNotifications();
      } else {
        setNotifications([]);
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, [collectionName]);

  const handleSelectNotification = async (notification: NotificationItem) => {
    const wasNew = notification.isNew;
    const target = await resolveNotificationTarget(notification);

    if (wasNew && userId) {
      await updateNotificationAsRead(notification.id, userId, collectionName);

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, isNew: false } : n,
        ),
      );
    }

    if (target) {
      navigate(target.path, target.state ? { state: target.state } : undefined);
    }
    // No known target (old notification missing type/requestDetailId) —
    // just mark it read, nothing else. No inline expand.
  };

  return (
    <div>
      <h1 className="text-xl md:text-2xl font-bold text-grease-100 mb-4 md:mb-6">Notifications</h1>

      <div className="w-full">
        {/* Left - Notification List */}
        <div className="w-full">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {notifications.length > 0 ? (
              <div>
                {paginatedNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleSelectNotification(notification)}
                    className="relative flex items-start gap-3 px-5 py-4 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-gray-600 text-sm font-bold">
                        {getInitials(notification.title || notification.responderName)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-grease-100 truncate">
                          {notification.title || notification.responderName}
                        </p>
                        {notification.isNew && (
                          <span className="flex-shrink-0 w-2 h-2 rounded-full bg-light-yellow"></span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {stripFlightMention(
                          notification.message ||
                            `${notification.responderName} has answered your request`,
                          notification.flightNumber,
                        )}
                      </p>
                      {(() => {
                        // "updated_answer_available" already bakes its own
                        // flight number/date into the message — skip the
                        // meta line there so the date isn't shown twice.
                        const metaLine =
                          notification.type === "updated_answer_available"
                            ? ""
                            : buildNotificationMetaLine(
                                notification.flightNumber,
                                notification.time,
                              );
                        return metaLine ? (
                          <p className="text-[11px] text-gray-400 mt-1">
                            {metaLine}
                          </p>
                        ) : null;
                      })()}
                    </div>

                    {/* Time & Arrow */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">
                        {notification.time ? formatTimeAgo(notification.time) : ""}
                      </span>
                      <i className="st-arrow-right text-xs text-gray-300"></i>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-60">
                <p className="text-gray-400 text-sm">No notifications</p>
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalCount={notifications.length}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              showTotal
            />
          )}
        </div>

        {/* Right - Notification Detail (hidden for now) */}
        {/* <div className="flex-1 min-w-0">
          {selectedNotification ? (
            <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
              {/* Header with avatar and name *}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-200 text-gray-600 text-sm font-bold">
                    {getInitials(selectedNotification.responderName)}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-grease-100">
                      {selectedNotification.responderName}
                    </p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600 p-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <circle cx="10" cy="4" r="1.5" />
                    <circle cx="10" cy="10" r="1.5" />
                    <circle cx="10" cy="16" r="1.5" />
                  </svg>
                </button>
              </div>

              {/* Message *}
              <div className="mt-6">
                <p className="text-sm text-gray-600 leading-relaxed break-words">
                  {selectedNotification.message}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 flex flex-col items-center justify-center min-h-[300px] h-full p-6">
              <img
                src={NotifImg}
                alt="No notification selected"
                className="w-full max-w-[200px] h-auto"
              />
              <p className="text-grease-100 font-semibold text-base mt-4">You're all caught up</p>
              <p className="text-gray-400 text-sm mt-1 text-center">
                Choose a notification to see more<br />details here
              </p>
            </div>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default Notifications;
